import { create } from 'zustand';
import type { StepTarget } from '../../api/partner';
import type { StepIn } from '../../api/types';
import { PENDING_KEY } from '../../config';
import { safeStorage } from '../../lib/storage';

/** A step pressed on the phone but not yet accepted by the server (offline, app closed mid-way). */
export interface PendingStep {
  id: string;
  target: StepTarget;
  body: StepIn;
  created_at: string;
}

export interface PendingStore {
  list: () => PendingStep[];
  put: (p: PendingStep) => void;
  remove: (id: string) => void;
}

function read(): PendingStep[] {
  try {
    const v = JSON.parse(safeStorage.get(PENDING_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as PendingStep[]).filter((x) => x && typeof x.id === 'string' && x.target && x.body) : [];
  } catch {
    return [];
  }
}

/**
 * Reactive state for the «not sent yet» banner. `busy` — ids an open step sheet is sending right now (it writes
 * the step ahead to storage before uploading): the banner neither counts nor re-sends those, otherwise it showed
 * «waiting to send» with «Send now» during an ordinary upload and a tap could post the same step twice.
 */
export const usePending = create<{ items: PendingStep[]; busy: string[] }>(() => ({ items: read(), busy: [] }));

export function markBusy(id: string): void {
  const b = usePending.getState().busy;
  if (!b.includes(id)) usePending.setState({ busy: [...b, id] });
}

export function unmarkBusy(id: string): void {
  usePending.setState({ busy: usePending.getState().busy.filter((x) => x !== id) });
}

/** Steps really waiting for the network (not the ones a sheet is sending this moment). */
export function waitingCount(s: { items: PendingStep[]; busy: string[] }): number {
  return s.items.filter((x) => !s.busy.includes(x.id)).length;
}

function write(items: PendingStep[]) {
  safeStorage.set(PENDING_KEY, JSON.stringify(items));
  usePending.setState({ items });
}

export const pendingStore: PendingStore = {
  list: () => read(),
  put(p) {
    write([...read().filter((x) => x.id !== p.id), p]);
  },
  remove(id) {
    write(read().filter((x) => x.id !== id));
  },
};

/** In-memory store for tests. */
export function memoryStore(): PendingStore & { items: PendingStep[] } {
  const s = {
    items: [] as PendingStep[],
    list: () => [...s.items],
    put(p: PendingStep) {
      s.items = [...s.items.filter((x) => x.id !== p.id), p];
    },
    remove(id: string) {
      s.items = s.items.filter((x) => x.id !== id);
    },
  };
  return s;
}
